import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ILongSteelOrderItem } from '../../../../../store/ducks/long-steel-shopping-cart/types';

import {
  Container,
  MenuTable,
  ProductNotFoundAlertContainer,
  HeaderContainer,
  Title,
  Subtitle
} from './styles';

import { Button, Icon } from '@gerdau/hefesto';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import SearchedItemsCodeEnum from './enum/SearchedItemsCodeEnum';
import SpreadsheetTableMvp from '../SpreadsheetTableMvp';
import { IOrderEntryPreference } from '../../../../../store/ducks/user/types';
import { RcFile } from 'antd/es/upload/interface';
import { cleanSpreadSheetMVP } from '../../../../../store/ducks/long-steel-order-item-search/actions';
import { SPREADSHEET_PRODUCT_NOT_FOUND } from '../../../../../store/ducks/long-steel-order-item-search/types';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';
import { MultProductsResponse } from '../../../../../services/MVP/useProductForCustomer';
import useDebounce from '../../../../../hooks/useDebounce';
import { GetFlag } from '../../../../../utils/featureFlag';

interface SearchedListMvpProps {
  searchedItems: ILongSteelOrderItem[];
  foundItens: number;
  notFoundItens: number;
  onRemoveAllItems: () => void;
  onRemoveSearchedItem(productIndex: number): void;
  onAddSearchedItem(items: ILongSteelOrderItem[]): void;
  userOrderEntryPreference: IOrderEntryPreference;
  excededLimit?: boolean;
  magentoProducts: MultProductsResponse[] | null;
  loading: boolean;
  cleanMagentoProducts: () => void;
  onFileUploaded: (file: RcFile) => void;
  internalCodeSkus: {
    [key: string]: string;
  };
}

interface productTableProps {
  key: number;
  product: string;
  codProduct: string;
  description: string;
  quantity: string | number;
  measureUnit: string;
  desiredDate: string;
  hasPrice: boolean;
}

interface productErrorProps {
  key?: number;
  product?: string;
  message: string;
  code?: string;
  prefix?: string;
}

export const SelfSearchedListMvp: React.FC<SearchedListMvpProps> = ({
  searchedItems,
  userOrderEntryPreference,
  onRemoveSearchedItem,
  onRemoveAllItems,
  excededLimit,
  magentoProducts,
  loading,
  cleanMagentoProducts,
  onFileUploaded,
  internalCodeSkus
}) => {
  const newSearchItemsEndpointFlag = GetFlag('new_search_items_endpoint');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dataProducts, setDataProducts] = useState<productTableProps[]>([]);
  const [dataProductsErrors, setDataProductsErrors] = useState<
    productErrorProps[]
  >([]);

  const productNotFound = () => {
    dispatch(
      addToast({
        message:
          'Nenhum produto encontrado para os códigos de materiais informados.',
        type: EToastTypes.DANGER,
        durationInMS: 5000,
        spaceToTop: 80
      })
    );
    dispatch(
      sendTagging({
        event: 'nenhum_produto_encontrado',
        screenName: 'planilha',
        mensagem:
          'Nenhum produto encontrado para os códigos de materiais informados.'
      })
    );
  };

  const dispatch = useDispatch();
  const debounceNotFound = useDebounce(productNotFound, 500);

  const [codeIsInternal, setCodeIsInternal] = useState<boolean>(false);

  useEffect(() => {
    try {
      const products: productTableProps[] = [];
      const productsWithErrors: productErrorProps[] = [];

      if (Array.isArray(magentoProducts)) {
        searchedItems.forEach((item: ILongSteelOrderItem, index: number) => {
          const hasInternalCode =
            userOrderEntryPreference &&
            userOrderEntryPreference?.codeType === 'CLIENT_SKU' &&
            item.customerSku.length > 0;
          setCodeIsInternal(hasInternalCode);

          const itemSkuInternalCode = newSearchItemsEndpointFlag
            ? item?.customerSku || ''
            : internalCodeSkus[item.sku];

          const itemSku = hasInternalCode ? itemSkuInternalCode : item.sku;

          const magentoItem = magentoProducts?.find((prd) =>
            newSearchItemsEndpointFlag &&
            hasInternalCode &&
            prd?.extension_attributes?.internal_code
              ? prd.extension_attributes.internal_code === itemSku
              : prd.sku === itemSku
          );
          const hasPrice = Number(magentoItem?.price?.creditPrice) > 0;

          // let mu = magentoItem?.price?.um;
          let mu = item.measureUnit ?? '';

          const productOptions = magentoItem?.productOptions;

          const currentDestination = magentoItem?.price?.destination;

          const hasMeasureUnitOptions =
            !!productOptions &&
            !!currentDestination &&
            !!productOptions[currentDestination] &&
            !!productOptions[currentDestination].measureUnits &&
            productOptions[currentDestination].measureUnits.length > 0;

          if (hasMeasureUnitOptions && productOptions && currentDestination) {
            // const measureUnitAvailabel =
            //   productOptions[currentDestination].measureUnits;

            // const hasMeasureUnit = measureUnitAvailabel.some(
            //   (measure: MeasureUnit) => measure.measure === item.measureUnit
            // );

            // // É preciso devolver a unidade de medida válida que vem na resposta do magento. caso contrário quebra o cart
            // mu = hasMeasureUnit
            //   ? (item.measureUnit as EMeasureUnit)
            //   : magentoItem?.price.um;

            mu = item.measureUnit ?? '';
          }

          products.push({
            key: index,
            product: hasInternalCode ? itemSkuInternalCode : itemSku,
            codProduct: item.customerPurchaseItem?.length
              ? Number(item.customerPurchaseItem).toString()
              : '-',
            description: magentoItem?.name?.length
              ? magentoItem?.name
              : SPREADSHEET_PRODUCT_NOT_FOUND,
            quantity: item.quantity ? String(item.quantity) : '-',
            measureUnit: mu ?? '-',
            desiredDate: item.desiredDate ?? '-',
            hasPrice
          });

          if (
            !item.found ||
            !magentoItem?.name?.length ||
            !mu ||
            item.description === SPREADSHEET_PRODUCT_NOT_FOUND ||
            !hasPrice
          ) {
            productsWithErrors.push({
              key: index,
              product: itemSku,
              message: item.message,
              code: hasPrice && item.code ? item.code : '10'
            });
          }
        });

        if (products.length === productsWithErrors.length) {
          debounceNotFound();
          dispatch(cleanSpreadSheetMVP());
          return;
        }

        if (products.find((product) => product.codProduct.length > 10)) {
          productsWithErrors.push({
            key: productsWithErrors.length + 1,
            message:
              'Um ou mais produtos excedeu o limite de 10 caracteres do número da linha.',
            prefix: '',
            product: ''
          });
        }

        setDataProducts(products.filter((prd) => prd.hasPrice).slice(0, 30));
        setDataProductsErrors(productsWithErrors.slice(0, 30));
      }
    } catch (e) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar informações do(s) item(s)',
          durationInMS: 5000,
          labelActionOne: 'Fechar'
        })
      );
    }
  }, [searchedItems, magentoProducts, loading]);

  const ProductNotFoundAlert = ({
    code,
    product = 'não identificado',
    message,
    prefix = 'O produto'
  }: {
    code?: string;
    product?: string;
    message: string;
    prefix?: string;
  }) => {
    const errorMsg =
      code === SearchedItemsCodeEnum.NOT_FOUND
        ? ' não foi encontrado.'
        : message;
    return (
      <ProductNotFoundAlertContainer>
        <Icon option="InfoCircle" variant="warning" size="sm" />
        {(product && `${prefix ?? ''} ${product}`.trim()) || ''}
        {errorMsg}
      </ProductNotFoundAlertContainer>
    );
  };

  const handleUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const HiddenInput = () => {
    return (
      <input
        key={JSON.stringify(userOrderEntryPreference)}
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xls,.xlsx"
        value={undefined}
        onChange={(event) => {
          const files = event.target.files;
          if (files && files.length > 0) {
            cleanMagentoProducts();
            onFileUploaded(files[0] as RcFile);
          }
        }}
      />
    );
  };

  useEffect(() => {
    const msg =
      'Sua planilha excedeu o limite de produtos. Foram adicionados na tabela os 30 primeiros.';
    if (
      dataProducts.length > 0 &&
      excededLimit &&
      !dataProductsErrors.find(({ message }) => message === msg)
    ) {
      dispatch(
        sendTagging({
          event: 'limite_produtos_excedido',
          screenName: 'enviar_planilha',
          etapa: 'planilha_carregada'
        })
      );

      setDataProductsErrors([
        ...dataProductsErrors,
        {
          key: dataProductsErrors.length + 1,
          message: msg,
          prefix: '',
          product: ''
        }
      ]);
    }
  }, [excededLimit, dataProductsErrors]);

  return (
    <Container>
      <HeaderContainer>
        <Title>Sua planilha</Title>

        <Subtitle>
          Você poderá alterar os dados dos produtos depois que avançar para o
          carrinho.
        </Subtitle>

        {dataProductsErrors.map((data: productErrorProps, index: number) => (
          <ProductNotFoundAlert
            key={index}
            code={data.code}
            message={data.message}
            product={data?.product}
          />
        ))}
      </HeaderContainer>
      <MenuTable>
        <Button
          onClick={handleUploadFile}
          name="removeAllItems"
          ariaLabel="removeAllItems"
          variant="secondary"
        >
          Reenviar planilha
        </Button>
        {dataProducts && dataProducts.length > 0 && (
          <Button
            onClick={onRemoveAllItems}
            name="removeAllItems"
            ariaLabel="removeAllItems"
            variant="danger"
          >
            Excluir todos os produtos
          </Button>
        )}
      </MenuTable>
      <SpreadsheetTableMvp
        onRemoveSearchedItem={onRemoveSearchedItem}
        products={dataProducts}
        codeIsInternal={codeIsInternal}
      />
      <HiddenInput />
    </Container>
  );
};
